import http from 'node:http';
import https from 'node:https';
import type { AddressInfo, Socket } from 'node:net';
import { AsyncLocalStorage } from 'node:async_hooks';
import wsGateway from './gateway/ws/core.ts';
import { GatewayManager } from './gateway/gateway-manager.ts';
import { compose } from './common/compose.ts';
import {
    ExtendedHttpIncomingMessage,
    ExtendedHttpServerResponse,
    HttpServerRequest,
    HttpServerResponse
} from './server/exchange.ts';
import getLogger from './logger.ts';
import { addressAndPort, localIp, portRange } from './server/address.ts';
import * as monitoring from './server/monitoring.ts';
import type { ServerConfig } from '../types/config.d.ts';
import { GatewayServer } from '@interopio/gateway-server';
import serverHeader from './server/server-header.ts';
import { configure, type RouteConfig } from './app/route.ts';
import { httpSecurity } from './app/auth.ts';
import type { ServerConfigurer, ServerHttpResponse, ServerWebExchange } from '../types/web/server.d.ts';
import { HttpHeadResponseDecorator } from './server/handler.ts';
import { initRoute } from './server/ws.ts';
import { type SocketRoute, webSockets } from './server/socket.ts';
import { HttpStatus } from './http/status.ts';
import { secureContextOptions } from './server/ssl/config.ts';
import codecConfig from './http/codec/config.ts';
import { gatewayManagementServer } from './manage/server.ts';

const logger = getLogger('app');

type RequestListener<
    Request extends typeof ExtendedHttpIncomingMessage = typeof ExtendedHttpIncomingMessage,
    Response extends typeof ExtendedHttpServerResponse<InstanceType<Request>> = typeof ExtendedHttpServerResponse,
> = (req: InstanceType<Request>, resOrUpgradeHead: (InstanceType<Response> & { req: InstanceType<Request> }) | Buffer<ArrayBufferLike>) => void;

type ServerOptions = http.ServerOptions<typeof ExtendedHttpIncomingMessage, typeof ExtendedHttpServerResponse>;

async function createListener(builder: WebHttpHandlerBuilder,
                              onSocketError: (err: Error) => void): Promise<RequestListener> {

    const httpHandler = builder.build();

    return async (req: ExtendedHttpIncomingMessage, resOrUpgradeHead: ExtendedHttpServerResponse | Buffer<ArrayBufferLike>) => {
        req.socket.addListener('error', onSocketError);
        let res: ExtendedHttpServerResponse;
        if (resOrUpgradeHead instanceof ExtendedHttpServerResponse) {
            res = resOrUpgradeHead;
        }
        else {
            req.upgradeHead = resOrUpgradeHead;
            res = new ExtendedHttpServerResponse(req);
            res.assignSocket(req.socket);
        }
        try {
            const request = new HttpServerRequest(req);
            const response = new HttpServerResponse(res);
            const decoratedResponse: ServerHttpResponse = request.method === 'HEAD' ? new HttpHeadResponseDecorator(response) : response;
            const hp: Promise<void> = httpHandler(request, decoratedResponse)
            try {
                await hp;
                if (logger.enabledFor('trace')) {
                    logger.debug(`${request.id} handling completed.`);
                }
            }
            catch (e) {
                if (e instanceof Error && e['code'] === 'ERR_INVALID_URL') {
                    throw e; // Let the outer catch handle this and return 400 Bad Request
                }
                if (logger.enabledFor('trace')) {
                    logger.debug(`${request.id} failed to complete: ${e instanceof Error ? e.message : e}`);
                }
            }
        }
        catch (e) {
            if (e instanceof Error && e['code'] === 'ERR_INVALID_URL') {
                if (logger.enabledFor('debug')) {
                    logger.debug(`failed to get request URI: ${e.message}`);
                }
                res.statusCode = 400; // Bad Request
                res.end(http.STATUS_CODES[400]);
                return Promise.resolve();
            }
            throw e;
        }
    };
}

function promisify<T>(fn: (callback?: (err?: Error) => void) => T): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const r = fn((err?: Error) => {
            if (err) {
                reject(err);
            } else {
                resolve(r);
            }
        });
    });
}

function memoryMonitor(config?: ServerConfig['memory']) {
    if (config) {
        return monitoring.start({
            memoryLimit: config.memory_limit,
            dumpLocation: config.dump_location,
            dumpPrefix: config.dump_prefix,
            reportInterval: config.report_interval,
            maxBackups: config.max_backups
        });
    }
}

import info from '@interopio/gateway-server/package.json' with { type: 'json' };
import welcomePage from './server/welcome-page.ts';
import { WebHttpHandlerBuilder } from './web/server/adapter.ts';
import { DefaultErrorAttributes, DefaultWebErrorHandler } from './server/error.ts';
import { WebFilterChainProxy } from './server/security/web.ts';
import { ResponseStatusError } from './web/server/error.ts';
import { DispatcherHandler } from './web/async/handler.ts';
import {
    HandlerFunctionAdapter,
    RouterFunctionMapping,
    ServerResponseResultHandler
} from './web/async/fn/server/handler.ts';
export const VERSION = `${info.name} - v${info.version}`;

async function initBuilder(context: RouteConfig) {
    const storage = context.storage;
    const security = await httpSecurity(context);
    // websocket upgrade handler
    const sockets = webSockets(context);
    const codecConfigurer = codecConfig.serverConfigurer.create();
    codecConfigurer.defaultCodecs.enableLoggingRequestDetails(true);
    const welcomePageRouterFunctionMapping = new RouterFunctionMapping();
    // welcomePageRouterFunctionMapping.init(await welcomePage(context.resourcesConfig?.locations));
    const h = new DispatcherHandler(
        [welcomePageRouterFunctionMapping],
        [new HandlerFunctionAdapter()],
        [new ServerResponseResultHandler(codecConfigurer.writers)]
    )
    const handler = {
        handle: compose<ServerWebExchange>(
            serverHeader(VERSION, context.serverHeader),
            ...sockets,
            ...context.middleware,
            // health check
            async ({ request, response }, next) => {
                if (request.method === 'GET' && request.path === '/health') {
                    response.setStatusCode(HttpStatus.OK);
                    const buffer = Buffer.from('UP', 'utf-8');
                    response.headers.set('Content-Type', 'text/plain; charset=utf-8');
                    await response.body(buffer);
                }
                else {
                    await next();
                }
            },
            await welcomePage(context.resourcesConfig?.locations),
            // not found
            async ({ response }, _next) => {
                throw new ResponseStatusError(HttpStatus.NOT_FOUND);
                // response.setStatusCode(HttpStatus.NOT_FOUND);
                // await response.end();
            }
        )
    };

    return new WebHttpHandlerBuilder(handler)
        .filter(new WebFilterChainProxy(security))
        .codecConfigurer(codecConfigurer)
        .exceptionHandler(
            new DefaultWebErrorHandler(
                codecConfigurer.readers,
                codecConfigurer.writers,
                new DefaultErrorAttributes()))
        .storage(storage);
}

export const Factory = async (options: ServerConfig): Promise<GatewayServer.Server> => {
    const ssl = options.ssl;
    const host = options.host;

    // Build serverCertificate config if auth.x509.key is provided
    const serverCertificate = options.auth?.x509?.key ? {
        host: host ?? 'localhost',
        key: options.auth.x509.key,
        passphrase: options.auth.x509.passphrase
    } : undefined;

    const createServer = ssl
        ? (options: ServerOptions, handler: RequestListener) => https.createServer({...options, ...secureContextOptions(ssl, serverCertificate)}, handler)
        : (options: ServerOptions, handler: RequestListener) => http.createServer(options, handler);
    const monitor = memoryMonitor(options.memory);
    const context: RouteConfig = {
        middleware: [],
        corsConfig: options.cors,
        cors: [],
        authConfig: options.auth,
        authorize: [],
        storage: new AsyncLocalStorage<{ exchange: ServerWebExchange }>(),
        sockets: new Map<string, SocketRoute>(),
        resourcesConfig: options.resources,
    };

    // Create gateway manager to handle default and per-principal gateway instances
    const gatewayManager = new GatewayManager({
        baseConfig: { ...(options.gateway) },
        scope: options.gateway?.scope ?? 'principal'
    });

    if (options.gateway) {
        const path = options.gateway.route ? (options.gateway.route === '/' ? undefined : options.gateway.route) : undefined;

        await configure(async (configurer: ServerConfigurer) => {
            configurer.socket({ path, factory: wsGateway.bind(gatewayManager), options: options.gateway })
        }, options, context);

    }
    if (options.app) {
        await configure(options.app, options, context)
    }

    const ports = portRange(options.port ?? 0);
    const onSocketError = (err: Error) => logger.error(`socket error: ${err}`, err);
    const builder = await initBuilder(context);

    const listener = await createListener(builder, onSocketError);

    const serverP = new Promise<http.Server<typeof ExtendedHttpIncomingMessage, typeof ExtendedHttpServerResponse>>((resolve, reject) => {

        const server = createServer({
            IncomingMessage: ExtendedHttpIncomingMessage,
            ServerResponse: ExtendedHttpServerResponse,
            ...options.http
        }, listener);

        server.on('error', (e: Error) => {
            if (e['code'] === 'EADDRINUSE') {
                logger.debug(`port ${e['port']} already in use on address ${e['address']}`);
                const {value: port} = ports.next();
                if (port) {
                    logger.info(`retry starting server on port ${port} and host ${host ?? '<unspecified>'}`);
                    server.close();
                    server.listen(port, host);
                } else {
                    logger.warn(`all configured port(s) ${options.port} are in use. closing...`);
                    server.close();
                    reject(e);
                }
            } else {
                logger.error(`server error: ${e.message}`, e);
                reject(e);
            }
        });
        server
            .on('listening', async () => {
                const info = server.address() as AddressInfo;

                for (const [path, route] of context.sockets) {
                    const endpoint = `${ssl ? 'wss' : 'ws'}://${localIp}:${info.port}${path}`;
                    await initRoute(path, route, endpoint, context.storage, onSocketError);
                }
                logger.info(`http server listening on ${ ssl ? 'https' : 'http' }://${addressAndPort(info)}`);
                resolve(server);
            });
        server
            .on('upgrade', (req: ExtendedHttpIncomingMessage, _socket: Socket, head: Buffer<ArrayBufferLike>) => {
                try {
                    listener(req, head);
                } catch (err) {
                    logger.error(`upgrade error: ${err}`, err);
                }
            })
            .on('close', async () => {
                logger.info(`http server closed.`);
            });
        try {
            const {value: port} = ports.next();
            server.listen(port, host);
        } catch (e) {
            logger.error(`error starting web socket server`, e);
            reject(e instanceof Error ? e : new Error(`listen failed: ${e}`));
        }
    });
    const server = await serverP;
    const closeActions = new Array<() => Promise<void>>();
    const closeSockets = async () => {
        for (const [path, route] of context.sockets) {
            try {
                if (route.close !== undefined) {
                    await route.close();
                }
            } catch (e) {
                logger.warn(`error closing route ${path}`, e);
            }
        }
    };
    const closeServer = async () => {
        await promisify(cb => {
            server.closeAllConnections();
            server.close(cb);
        });
    };
    closeActions.push(closeSockets);
    closeActions.push(closeServer);

    closeActions.push(async () => {
        if (monitor) {
            await monitoring.stop(monitor);
        }
    });
    closeActions.push(async () => {
        // Stop all gateways (default + per-principal)
        await gatewayManager.stop();
    });
    const fullServer = new class implements GatewayServer.Server {
        // Expose the gateway manager which implements ScopedGateway
        readonly gateway = gatewayManager;

        get address(): AddressInfo | null {
            const address = server.address();
            return typeof address === 'object' ? address : null;
        }

        async close(): Promise<void> {
            const errors = new Array<unknown>();
            for (const action of closeActions) {
                try {
                    await action();
                }
                catch (e) {
                    errors.push(e);
                    logger.warn(`error during server shutdown`, e);
                }
            }
            if (errors.length === 1) {
                throw errors[0];
            }
            else if (errors.length > 1) {
                throw new AggregateError(errors, 'multiple errors during server shutdown');
            }
        }
    }

    if (options.management?.server.path) {
        const managementServer =
            await gatewayManagementServer({
                options: options.management.server,
                info: async () => ({
                    pid: process.pid,
                    type: 'gateway-server',
                    ...(gatewayManager.info())
                }),
                logger: logger.child('management'),
                shutdown: options.management.commands?.['shutdown'].enabled ? async () => {
                    if (logger.enabledFor('info')) {
                        logger.info(`stopping gateway-server...`);
                    }
                    await fullServer.close();
                } : undefined
            });
        closeActions.push(async () => {
            await promisify((cb) => {
                managementServer.close(cb);
            });
        });
    }

    return fullServer;
}
